import { z } from 'zod';
import { paginatedSchema, paginationQuerySchema } from './pagination';

// ---------------------------------------------------------------------------------------------
// Reuniões: agenda e registro (PRD §11.25, RF-39; DEC-049).

export const MEETING_TYPES = ['CRITICAL_ANALYSIS', 'OTHER'] as const;
export type MeetingTypeName = (typeof MEETING_TYPES)[number];
export const MEETING_TYPE_LABELS: Record<MeetingTypeName, string> = {
  CRITICAL_ANALYSIS: 'Análise crítica',
  OTHER: 'Outra reunião',
};

/** Nível da reunião (PRD §11.25). */
export const MEETING_LEVELS = ['BOARD', 'MANAGEMENT', 'OPERATION'] as const;
export type MeetingLevelName = (typeof MEETING_LEVELS)[number];
export const MEETING_LEVEL_LABELS: Record<MeetingLevelName, string> = {
  BOARD: 'Diretoria',
  MANAGEMENT: 'Gerência',
  OPERATION: 'Operação',
};

/** Agendada → em andamento → encerrada (encerrar exige o registro). */
export const MEETING_STATUSES = ['SCHEDULED', 'IN_PROGRESS', 'CLOSED'] as const;
export type MeetingStatusName = (typeof MEETING_STATUSES)[number];
export const MEETING_STATUS_LABELS: Record<MeetingStatusName, string> = {
  SCHEDULED: 'Agendada',
  IN_PROGRESS: 'Em andamento',
  CLOSED: 'Encerrada',
};

export const MEETING_TITLE_MAX = 160;
export const MEETING_TEXT_MAX = 10_000;
export const MEETING_PARTICIPANTS_MAX = 60;
export const MEETING_DURATION_MIN = 15;
export const MEETING_DURATION_MAX = 8 * 60;

/** Pendente: agendada cujo término (início + duração) já passou sem ser iniciada (DEC-049). */
export function meetingPending(
  m: { status: MeetingStatusName; startsAt: string | Date; durationMinutes: number },
  now: Date = new Date(),
): boolean {
  const start = typeof m.startsAt === 'string' ? new Date(m.startsAt) : m.startsAt;
  return m.status === 'SCHEDULED' && start.getTime() + m.durationMinutes * 60_000 < now.getTime();
}

const ref = z.object({ id: z.string().uuid(), name: z.string() });

const MEETING_TIME_ZONE = 'America/Sao_Paulo';

/** Data e hora locais (São Paulo) de um instante. */
function localParts(date: Date) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: MEETING_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === type)!.value);
  return { y: get('year'), m: get('month'), d: get('day'), h: get('hour'), min: get('minute') };
}

/**
 * Próxima reunião sugerida (DEC-051): um mês depois, no mesmo horário de São Paulo; quando o dia não existe no mês
 * seguinte, o último dia dele. Devolve ISO em UTC.
 */
export function nextMonthStart(startsAt: string | Date): string {
  const start = typeof startsAt === 'string' ? new Date(startsAt) : startsAt;
  const p = localParts(start);
  const y = p.m === 12 ? p.y + 1 : p.y;
  const m = p.m === 12 ? 1 : p.m + 1;
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const d = Math.min(p.d, lastDay);
  // O instante cujo horário local é o pedido: parte do horário "como se fosse UTC" e corrige pelo fuso do dia.
  const guess = Date.UTC(y, m - 1, d, p.h, p.min);
  const shown = localParts(new Date(guess));
  const offset = Date.UTC(shown.y, shown.m - 1, shown.d, shown.h, shown.min) - guess;
  return new Date(guess - offset).toISOString();
}

export const meetingSchema = z.object({
  id: z.string().uuid(),
  type: z.enum(MEETING_TYPES),
  level: z.enum(MEETING_LEVELS),
  title: z.string(),
  /** Início (data e hora, ISO). */
  startsAt: z.string(),
  durationMinutes: z.number().int(),
  department: ref.nullable(),
  businessUnit: ref.nullable(),
  participants: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
  agenda: z.string().nullable(),
  record: z.string().nullable(),
  status: z.enum(MEETING_STATUSES),
  /** Agendada vencida sem ser iniciada (calculado). */
  pending: z.boolean(),
  startedAt: z.string().nullable(),
  closedAt: z.string().nullable(),
  /** Quando a ata foi gerada (no encerramento — DEC-051). */
  minutesAt: z.string().nullable(),
  createdByName: z.string(),
  createdAt: z.string(),
  /** Quem pede pode alterar (módulo e escopo por área/unidade — DEC-049). */
  canEdit: z.boolean(),
});
export type Meeting = z.infer<typeof meetingSchema>;

export const meetingListSchema = paginatedSchema(meetingSchema);

export const meetingListQuerySchema = paginationQuerySchema.extend({
  status: z.enum(MEETING_STATUSES).optional(),
  type: z.enum(MEETING_TYPES).optional(),
  level: z.enum(MEETING_LEVELS).optional(),
  /** Só as pendentes (agendadas vencidas). */
  pending: z.enum(['true', 'false']).optional(),
  /** Período do início: de (inclusive) até (exclusive), data e hora com fuso (a tela manda o mês ou a semana local). */
  from: z.iso.datetime({ offset: true }).optional(),
  to: z.iso.datetime({ offset: true }).optional(),
  /** Ordem: próximas primeiro (asc) ou mais recentes primeiro (desc). */
  order: z.enum(['asc', 'desc']).default('asc'),
});
export type MeetingListQuery = z.input<typeof meetingListQuerySchema>;

const optionalText = (max: number, label: string) =>
  z
    .string()
    .trim()
    .max(max, `${label} pode ter no máximo ${max.toLocaleString('pt-BR')} caracteres.`)
    .nullable()
    .optional()
    .transform((v) => v || null);

export const meetingInputSchema = z.object({
  type: z.enum(MEETING_TYPES, { message: 'Escolha o tipo da reunião.' }),
  level: z.enum(MEETING_LEVELS, { message: 'Escolha o nível da reunião.' }),
  title: z
    .string({ message: 'Informe o título.' })
    .trim()
    .min(3, 'Informe o título (pelo menos 3 caracteres).')
    .max(MEETING_TITLE_MAX, 'Título longo demais.'),
  /** Início com data e hora (ISO com fuso). */
  startsAt: z.iso.datetime({ offset: true, message: 'Informe a data e a hora.' }),
  durationMinutes: z.coerce
    .number({ message: 'Informe a duração.' })
    .int('Informe a duração em minutos.')
    .min(MEETING_DURATION_MIN, `A reunião precisa ter pelo menos ${MEETING_DURATION_MIN} minutos.`)
    .max(MEETING_DURATION_MAX, 'A reunião pode ter no máximo 8 horas.'),
  departmentId: z
    .string()
    .uuid('Área inválida.')
    .nullable()
    .optional()
    .transform((v) => v ?? null),
  businessUnitId: z
    .string()
    .uuid('Unidade inválida.')
    .nullable()
    .optional()
    .transform((v) => v ?? null),
  participantIds: z
    .array(z.string().uuid('Participante inválido.'))
    .max(MEETING_PARTICIPANTS_MAX, `No máximo ${MEETING_PARTICIPANTS_MAX} participantes.`)
    .default([])
    .transform((ids) => [...new Set(ids)]),
  agenda: optionalText(MEETING_TEXT_MAX, 'A pauta'),
});
export type MeetingInput = z.input<typeof meetingInputSchema>;

/** Próxima reunião informada no encerramento (DEC-051). */
export const nextMeetingInputSchema = z.object({
  title: z
    .string({ message: 'Informe o título da próxima reunião.' })
    .trim()
    .min(3, 'Informe o título da próxima reunião (pelo menos 3 caracteres).')
    .max(MEETING_TITLE_MAX, 'Título longo demais.'),
  startsAt: z.iso.datetime({ offset: true, message: 'Informe a data e a hora da próxima reunião.' }),
});
export type NextMeetingInput = z.input<typeof nextMeetingInputSchema>;

/**
 * Iniciar ou encerrar; encerrar exige o registro (o que foi tratado) e gera a ata. A próxima reunião: na análise
 * crítica, é sempre agendada (sem `next`, um mês depois com o mesmo título); nas outras, só com `next`.
 */
export const meetingStatusInputSchema = z
  .object({
    status: z.enum(['IN_PROGRESS', 'CLOSED'], { message: 'Escolha iniciar ou encerrar.' }),
    record: optionalText(MEETING_TEXT_MAX, 'O registro'),
    next: nextMeetingInputSchema.nullable().optional(),
  })
  .refine((v) => v.status !== 'CLOSED' || (v.record !== null && v.record.length >= 10), {
    message: 'Para encerrar, escreva o registro do que foi tratado (pelo menos 10 caracteres).',
    path: ['record'],
  })
  .refine((v) => v.status === 'CLOSED' || !v.next, {
    message: 'A próxima reunião é agendada ao encerrar.',
    path: ['next'],
  });
export type MeetingStatusInput = z.input<typeof meetingStatusInputSchema>;

/** Registro durante a reunião (salvar sem encerrar). */
export const meetingRecordInputSchema = z.object({ record: optionalText(MEETING_TEXT_MAX, 'O registro') });

export const meetingOptionsSchema = z.object({
  people: z.array(z.object({ id: z.string().uuid(), name: z.string(), fluxus: z.boolean() })),
  departments: z.array(ref),
  businessUnits: z.array(ref),
});
export type MeetingOptions = z.infer<typeof meetingOptionsSchema>;
